import React, { useEffect, useRef } from 'react';
import { inject, observer } from 'mobx-react';
import styled from 'styled-components';

import { Helmet } from 'react-helmet-async';

import SpinnerComponent from '../Common/Spinners.js';
import LeftSidebar from '../Common/LeftSidebar.js';
import TournamentRow from './TournamentRow.js';

import { Multiselect } from 'multiselect-react-dropdown';

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  font-size: .85em;
  min-width: 20em;
  max-width: 75em;

  @media (max-width: 768px) {
    /* Mobile CSS changes go here */
    font-size: .85em;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 2em;
  min-height: 38em;
  max-width: 45em;
`;

const PageTitle = styled.div`
  font-size: 2em;
  font-weight: bold;
`;

const FiltersContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  margin-top: 1em;
  padding: .5em;
  border: 1px dashed #333;

  @media (max-width: 768px) {
    display: block;
  }
`;

const FilterContainerSingleSelect = styled.div`
  flex: 1;
  max-width: ${(props) => (props.$maxWidth ? props.$maxWidth : '12em')};
  margin: 5px;

  > div > div > div {
    padding: 0;
  }

  > div > div > div > span {
    color: #777;
    margin-bottom: 0;
    font-size: 1em;
  }

  > div > div > div > span > img {
    display: none;
  }

  > div > div > div > input {
    display: none;
  }

  & .chip {
    padding: 7px 7px 4px 7px;
    background-color: white;
    font-size: .8rem;
  }

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const Reset = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 0 .25rem;
  height: 30px;
  max-width: 6em;
  border: 1px solid #aaa;
  border-radius: 4px;
  padding: 0 .75em;
  background-color: #228fd8;
  color: white;
  font-size: .9rem;
  text-align: center;

  @media (max-width: 768px) {
    max-width: 100%;
    justify-content: center;
  }
`;

const ResetOverlay = styled.div`
  z-index: 100;
  margin-left: -6.75em;
  height: 100%;
  min-width: 6em;
  padding: .25em .75em;
  opacity: 0;
  cursor: pointer;
`;

const TournamentsHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 2em;
  margin-top: 1em;
  padding-right: 17px; // To cover width of scrollbar
  border-top: 1px solid black;
  border-bottom: 1px solid black;

  @media (max-width: 768px) {
    padding-right: 0;
  }
`;

const TournamentHeaderColumn = styled.div`
  flex: ${props => props.flex ? props.flex : '1'};
  text-align: ${props => props.align ? props.align : 'center'};
  padding: 4px;
  font-weight: bold;
  font-size: 1.2em;
`;

const TournamentsContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  border: 1px solid #ccc;
  max-height: 35em;
  overflow-y: scroll;
`;

const regionFilterOptions = [
  { region: 'any', label: 'All Regions', id: 'any' },
  { region: 'INTERNATIONAL', label: 'VCT / Global', id: 'international' },
  { region: 'BRAZIL', label: 'Brazil', id: 'brazil' },
  { region: 'EMEA', label: 'EMEA', id: 'emea' },
  { region: 'JAPAN', label: 'Japan', id: 'japan' },
  { region: 'KOREA', label: 'South Korea', id: 'korea' },
  { region: 'LATIN AMERICA', label: 'Latin America', id: 'latinAmerica' },
  { region: 'NORTH AMERICA', label: 'North America', id: 'northAmerica' },
  { region: 'OCEANIA', label: 'Oceania', id: 'oceania' },
  { region: 'SOUTHEAST ASIA', label: 'Southeast Asia', id: 'Southeast Asia' },
  { region: 'VIETNAM', label: 'Vietnam', id: 'vietnam' }
];

const tierFilterOptions = [
  { tier: 'any', label: 'Any Tier', id: 'any' },
  { tier: 'global', label: 'Tier 1: VCT / Global', id: 'global' },
  { tier: 'challengers', label: 'Tier 2: Challengers', id: 'challengers' },
  { tier: 'regionals', label: 'Tier 3: Regionals', id: 'regionals' },
  { tier: 'game-changers', label: 'Game-Changers', id: 'game-changers' }
];

const StatsNav = ({ AppStore }) => {
  const isSafari =
    navigator.userAgent.indexOf("Safari") > -1 &&
    navigator.userAgent.indexOf("Chrome") === -1;
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  )
    ? true
    : false;

  const regionRef = useRef(null);
  const tierRef = useRef(null);

  const route = 'statsNav';
  const didRouteChange = route !== AppStore.currentRoute.get();

  const tournaments = AppStore.statsTournamentsFiltered;
  const isTournamentsFetching = AppStore.isStatsTournamentsFetching.get();
  const tournamentsFiltering = AppStore.statsTournamentsFiltering.get();

  const pageTitle = `OE Valorant Stats`;
  const pageDescription = 'OE Valorant Stats - Navigation';

  const handleFilterSubmit = () => {
    AppStore.filterStatsTournaments();
  };

  const onChangeTier = (selectedList, selectedItem) => {
    AppStore.setStatsTournamentsFilterValue('tier', selectedItem);
    handleFilterSubmit();
  };

  const onChangeRegion = (selectedList, selectedItem) => {
    AppStore.setStatsTournamentsFilterValue('region', selectedItem);
    handleFilterSubmit();
  };

  const resetFilters = (event) => {
    event.preventDefault();
    tierRef.current.onSelectItem({ tier: 'any', label: 'Any Tier', id: 'any' });
    regionRef.current.onSelectItem({ region: 'any', label: 'All Regions', id: 'any' });
    handleFilterSubmit();
  }

  useEffect(() => {
    if (didRouteChange) {
      AppStore.setCurrentRoute(route);
    }

    if (!isTournamentsFetching && tournaments.length === 0) {
      AppStore.fetchStatsTournaments();
    }
  }, [
    AppStore,
    didRouteChange,
    AppStore.currentRoute,
    tournaments,
    isTournamentsFetching,
    tournamentsFiltering
  ]);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta
          name={pageTitle}
          content={pageDescription}
        />
      </Helmet>
      <LeftSidebar />
      <Wrapper className="mainContent">
        <Content>
          <PageTitle>Stats by Tournament</PageTitle>
          <FiltersContainer>
            <FilterContainerSingleSelect>
              <Multiselect
                id='tierFilter'
                options={tierFilterOptions}
                singleSelect={true}
                selectedValues={[AppStore.statsTournamentsFilters.tier]}
                onSelect={onChangeTier}
                onRemove={onChangeTier}
                closeIcon='cancel'
                closeOnSelect={true}
                displayValue='label'
                ref={tierRef}
              />
            </FilterContainerSingleSelect>
            <FilterContainerSingleSelect>
              <Multiselect
                id='regionFilter'
                options={regionFilterOptions}
                singleSelect={true}
                selectedValues={[AppStore.statsTournamentsFilters.region]}
                onSelect={onChangeRegion}
                onRemove={onChangeRegion}
                closeIcon='cancel'
                closeOnSelect={true}
                displayValue='label'
                ref={regionRef}
              />
            </FilterContainerSingleSelect>
            <Reset onClick={resetFilters}>
              <div style={{ 'minWidth': '6em' }}>Reset Filters</div>
              <ResetOverlay />
            </Reset>
          </FiltersContainer>
          <TournamentsHeader>
            <TournamentHeaderColumn flex={3} align='left'>Tournament</TournamentHeaderColumn>
            <TournamentHeaderColumn>Region</TournamentHeaderColumn>
            <TournamentHeaderColumn>Tier</TournamentHeaderColumn>
            <TournamentHeaderColumn>Stats</TournamentHeaderColumn>
          </TournamentsHeader>
          <TournamentsContainer>
            {(AppStore.isTournamentsFetching || didRouteChange) ? (
              <SpinnerComponent />
            ) : (
              tournaments.map((tournament, idx) => {
                return <TournamentRow key={tournament.id} tournamentData={tournament} rowIndex={idx} />
              })
            )}
          </TournamentsContainer>
        </Content>
      </Wrapper >
    </>
  );
};

export default inject("AppStore")(observer(StatsNav));
